export type HermesChoiceOption = {
  id: string;
  label: string;
  hint?: string;
};

export type HermesChoiceCard = {
  id: string;
  kind?: string;
  prompt: string;
  allow_multiple: boolean;
  options: HermesChoiceOption[];
};

export function hermesMessageChoicesFromPayload(
  toolPayload: Record<string, unknown> | null | undefined,
): HermesChoiceCard[] {
  if (!toolPayload || typeof toolPayload !== "object") return [];
  const raw = toolPayload.choices;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => normalizeChoice(item))
    .filter((item): item is HermesChoiceCard => item != null);
}

function normalizeChoice(raw: unknown): HermesChoiceCard | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = String(row.id ?? "").trim();
  const prompt = String(row.prompt ?? "").trim();
  if (!id || !prompt) return null;
  const optionsRaw = Array.isArray(row.options) ? row.options : [];
  const options = optionsRaw
    .map((opt) => {
      if (!opt || typeof opt !== "object") return null;
      const o = opt as Record<string, unknown>;
      const oid = String(o.id ?? "").trim();
      const label = String(o.label ?? "").trim();
      if (!oid || !label) return null;
      const hint = String(o.hint ?? "").trim();
      return hint ? { id: oid, label, hint } : { id: oid, label };
    })
    .filter((opt): opt is HermesChoiceOption => opt != null);
  if (options.length < 2) return null;

  return {
    id,
    kind: row.kind != null ? String(row.kind) : undefined,
    prompt,
    allow_multiple: row.allow_multiple !== false,
    options,
  };
}

/** 单张卡是否已选够（单选 1 项 / 多选至少 1 项） */
export function hermesChoiceSelectionComplete(
  choice: HermesChoiceCard,
  selectedIds: string[],
): boolean {
  if (choice.allow_multiple) return selectedIds.length > 0;
  return selectedIds.length === 1;
}

/** 同轮多张卡是否都已选完，可一次提交 */
export function hermesChoicesReadyToSubmit(
  choices: HermesChoiceCard[],
  selections: Record<string, string[]>,
): boolean {
  if (choices.length === 0) return false;
  return choices.every((choice) =>
    hermesChoiceSelectionComplete(choice, selections[choice.id] ?? []),
  );
}

/** 用户确认多选后写入对话的正文 */
export function formatHermesChoiceReply(choice: HermesChoiceCard, selectedIds: string[]): string {
  const selected = choice.options.filter((opt) => selectedIds.includes(opt.id));
  const labels = selected.map((opt) => opt.label).join("、");
  const ids = selected.map((opt) => opt.id).join(",");
  const kind = choice.kind ? `（${choice.kind}）` : "";
  return `已选择${kind}：${labels || "（无）"}\n[choice_id=${choice.id}; option_ids=${ids}]`;
}

/** 同轮多张卡：合成一条用户消息，避免一张一张提交把其它卡锁死 */
export function formatHermesChoicesReply(
  choices: HermesChoiceCard[],
  selections: Record<string, string[]>,
): string {
  const parts = choices.map((choice) =>
    formatHermesChoiceReply(choice, selections[choice.id] ?? []),
  );
  let body = parts.join("\n\n");
  if (choicesNeedKbGuide(choices, selections)) {
    body = `${body}\n\n${HERMES_KB_ENABLE_GUIDE}`;
  }
  return body;
}

export function choicesNeedKbGuide(
  choices: HermesChoiceCard[],
  selections: Record<string, string[]>,
): boolean {
  return choices.some((choice) => {
    if (choice.kind !== "kb_enable" && choice.kind !== "capability_upgrade") return false;
    const selected = selections[choice.id] ?? [];
    return selected.includes("enable_kb") || selected.includes("open_panel");
  });
}

export const HERMES_KB_ENABLE_GUIDE = [
  "如何开启写入：",
  "打开输入框下方 + 能力 → 找到企业或品牌 → 把知识库/资源库/旅程/官网改成完全控制或完全访问。",
  "改完后回到对话里说「已开启，请继续」。",
].join("\n");
